import {useMutation} from '@tanstack/react-query';
import {Form} from 'antd';
import {ReactElement, useCallback, useMemo} from 'react';

import {appRoute} from '../../../../../../app-route';
import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../../provider/locale/localization';
import {useSnackbar} from '../../../../../../provider/snackbar/snackbar-hook';
import {
    CustomReviewError,
    EditReviewFormPaylaodType,
    fetchEditReview,
    handleCustomReviewError,
    ReviewFormErrorType,
    ReviewPayloadType,
} from '../../../../../../service/reviews/custom-reviews';
import {ReviewCustomCompanyType} from '../../../../../../service/reviews/reviews-custom-compamies';
import {ReviewDataType} from '../../../../../../service/reviews/reviews-type';
import {useUrl} from '../../../../../../util/url-hook/url-hook';
import {deserializeReview, serializeEditReviewForm} from '../../../review-form/review-form-helper';
import {ReviewFormFieldsType} from '../../../review-form/review-form-type';
import {CustomReviewForm} from '../custom-review-form';

type PropsType = {
    review: ReviewDataType;
};

export function EditCustomReview(props: PropsType): ReactElement {
    const {review} = props;
    const [form] = Form.useForm<ReviewFormFieldsType>();
    const {snackbar} = useSnackbar();
    const {pushUrl} = useUrl();
    const {getLocalizedString} = useLocale();

    const handleCancel = useCallback(() => {
        form.resetFields();
    }, [form]);

    const {
        mutate: editReview,
        isLoading: isReviewFormInProgress,
        error: serverError,
    } = useMutation<ReviewPayloadType, ReviewFormErrorType, EditReviewFormPaylaodType>({
        mutationFn: (fields: EditReviewFormPaylaodType) => fetchEditReview(fields),
        onSuccess: () => {
            snackbar.success({
                message: <Locale stringKey="REVIEW__FORM__CREATED__SUCCESS_HEADER" />,
                description: <Locale stringKey="REVIEW__FORM__EDITED__SUCCESS_BODY" />,
            });

            pushUrl(appRoute.reviewsManagement.path);
        },
        onError: (error) => {
            if (error instanceof CustomReviewError) {
                snackbar.error((error as Error).message);
            } else {
                snackbar.error({
                    message: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />,
                    description: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG_DETAILS" />,
                });
            }

            handleCustomReviewError(error);
        },
    });

    const handleSubmit = useCallback(async () => {
        await form
            .validateFields()
            .then((values) => editReview(serializeEditReviewForm(values, review.id)))
            .catch(console.log);
    }, [editReview, form, review.id]);

    const initialCompany = useMemo(
        () =>
            review.company
                ? ({
                      id: review.company.id,
                      name: review.company.name,
                      address: review.company.address,
                  } as unknown as ReviewCustomCompanyType)
                : null,
        [review.company]
    );

    return (
        <CustomReviewForm
            editReview={review.id}
            form={form}
            handleCancel={handleCancel}
            handleSubmit={handleSubmit}
            initialCompany={initialCompany}
            initialValues={deserializeReview(review)}
            isReviewFormInProgress={isReviewFormInProgress}
            pageHeaderTitle={getLocalizedString('REVIEW__FORM__MODAL__HEADER__EDITING')}
            serverError={serverError}
        />
    );
}
